import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import LodgeDatePicker from "../components/LodgeDatePicker";
import { GraduationCap, Plus, Pencil, Trash2, X, Sparkles, HelpCircle } from "lucide-react";

/**
 * Faza 6 — Parcurs educațional pe grad (admin side).
 * Adminul gestionează curriculumul: lecții pe grad (1=Ucenic, 2=Calfă,
 * 3=Maestru), cu quiz opțional de verificare (prag 70%). Frații văd în
 * aplicația mobilă doar lecțiile gradului lor și ale gradelor inferioare.
 */
const EMPTY_FORM = { id: null, title: "", degree: 1, order: 0, body: "" };
const EMPTY_EXAM = { degree: 1, questions: [] };

function MentorshipSection() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [overview, setOverview] = useState([]);
  const [newTask, setNewTask] = useState({ 1: "", 2: "", 3: "" });

  const load = async () => {
    try {
      const [tk, ov] = await Promise.all([api.get("/edu/tasks"), api.get("/edu/mentorship/overview")]);
      setTasks(tk.data || []);
      setOverview(ov.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addTask = async (deg) => {
    const title = (newTask[deg] || "").trim();
    if (!title) return;
    try {
      await api.post("/edu/tasks", { degree: deg, title });
      setNewTask((s) => ({ ...s, [deg]: "" }));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`${t("edu.taskDeleteConfirm", "Ștergi acest task?")} «${task.title}»`)) return;
    try { await api.delete(`/edu/tasks/${task.id}`); await load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStarted = async (m, date) => {
    try {
      await api.put(`/members/${m.member_id}/degree-started`, { date: date || null });
      setOverview((rowsPrev) => rowsPrev.map((r) => (r.member_id === m.member_id ? { ...r, degree_started_at: date || null } : r)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const timeIn = (d) => {
    if (!d) return "—";
    const days = Math.max(0, Math.floor((Date.now() - new Date(`${d}T00:00:00`).getTime()) / 86400000));
    const months = Math.floor(days / 30);
    return months >= 1 ? t("edu.monthsInDegree", { count: months }) : t("edu.daysInDegree", { count: days });
  };

  return (
    <>
      <div className="mt-3 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
        {[1, 2, 3].map((deg) => {
          const degTasks = tasks.filter((x) => x.degree === deg);
          return (
            <div key={deg} className="lodge-card" style={{ padding: 16 }} data-testid={`edu-tasks-card-${deg}`}>
              <h3 style={{ fontWeight: 700, fontSize: "0.92rem" }}>{t(`planse.deg${deg}`)}</h3>
              {degTasks.length === 0 && (
                <p style={{ fontSize: "0.78rem", color: "#a09a88", fontStyle: "italic", marginTop: 6 }}>{t("edu.noTasks", "Niciun task pentru acest grad")}</p>
              )}
              {degTasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between gap-2" style={{ fontSize: "0.82rem", padding: "5px 0", borderBottom: "1px solid #efe9d8" }} data-testid={`edu-task-${task.id}`}>
                  <span>{task.title}</span>
                  <button className="btn-danger" onClick={() => removeTask(task)} data-testid={`edu-task-delete-${task.id}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              <div className="flex gap-2 mt-3">
                <input
                  className="input-lodge" style={{ fontSize: "0.8rem" }}
                  placeholder={t("edu.taskPlaceholder", "Task nou (ex. Lectura a 3 planșe)")}
                  value={newTask[deg] || ""}
                  onChange={(e) => setNewTask((s) => ({ ...s, [deg]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(deg); } }}
                  data-testid={`edu-task-input-${deg}`}
                />
                <button className="btn-primary-gold" onClick={() => addTask(deg)} data-testid={`edu-task-add-${deg}`}>
                  <Plus size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="mt-6" style={{ fontWeight: 700, fontSize: "0.85rem", letterSpacing: 0.5, color: "#8a7340" }}>
        {t("edu.membersProgress", "Progresul fraților")}
      </h3>
      <div className="lodge-card mt-2" style={{ padding: 0, overflowX: "auto" }} data-testid="edu-mentorship-table">
        <table style={{ width: "100%", fontSize: "0.82rem", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#faf7f0", textAlign: "left" }}>
              <th style={{ padding: "8px 12px" }}>{t("edu.colMember", "Frate")}</th>
              <th style={{ padding: "8px 12px" }}>{t("edu.colDegree", "Grad")}</th>
              <th style={{ padding: "8px 12px" }}>{t("edu.colTasks", "Task-uri")}</th>
              <th style={{ padding: "8px 12px" }}>{t("edu.colStarted", "Data gradului actual")}</th>
              <th style={{ padding: "8px 12px" }}>{t("edu.colTime", "Timp în grad")}</th>
            </tr>
          </thead>
          <tbody>
            {overview.length === 0 && (
              <tr><td colSpan={5} style={{ padding: "10px 12px", fontStyle: "italic", color: "#a09a88" }}>{t("edu.noMembers", "Niciun membru")}</td></tr>
            )}
            {overview.map((m) => (
              <tr key={m.member_id} style={{ borderTop: "1px solid #efe9d8" }} data-testid={`edu-mentorship-row-${m.member_id}`}>
                <td style={{ padding: "7px 12px", fontWeight: 600 }}>
                  {m.name}
                  {m.ready && (
                    <span style={{ marginLeft: 8, fontSize: "0.68rem", fontWeight: 800, color: "#3e6a3c", background: "#e8f2e6", padding: "2px 8px", borderRadius: 10 }} data-testid={`edu-ready-badge-${m.member_id}`}>
                      ✓ {t("edu.readyBadge", "Pregătit pentru examinare")}
                    </span>
                  )}
                </td>
                <td style={{ padding: "7px 12px" }}>{m.degree}°</td>
                <td style={{ padding: "7px 12px" }}>{m.tasks_total ? `${m.tasks_done}/${m.tasks_total}` : "—"}</td>
                <td style={{ padding: "7px 12px", minWidth: 160 }}>
                  <LodgeDatePicker
                    value={m.degree_started_at || ""}
                    onChange={(v) => setStarted(m, v)}
                    placeholder={t("edu.colStarted", "Data gradului actual")}
                    testId={`edu-degree-started-${m.member_id}`}
                  />
                </td>
                <td style={{ padding: "7px 12px" }}>{timeIn(m.degree_started_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function EducationPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [exams, setExams] = useState([]);
  const [showExam, setShowExam] = useState(false);
  const [examForm, setExamForm] = useState(EMPTY_EXAM);
  const [resultsFor, setResultsFor] = useState(null);
  const [results, setResults] = useState([]);

  const load = async () => {
    try {
      const [{ data }, ex] = await Promise.all([api.get("/edu/lessons"), api.get("/edu/exams")]);
      setRows(data || []);
      setExams(ex.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY_FORM }); setShowForm(true); };
  const openEdit = (l) => {
    setForm({ id: l.id, title: l.title, degree: l.degree, order: l.order || 0, body: l.body || "" });
    setShowForm(true);
  };

  const openExam = (deg) => {
    const ex = exams.find((e) => e.degree === deg);
    setExamForm({
      degree: deg,
      questions: (ex?.questions || []).map((q) => ({ q: q.q, options: (q.options || []).join("\n"), correct: q.correct || 0 })),
    });
    setShowExam(true);
  };

  const saveExam = async (e) => {
    e.preventDefault();
    const questions = [];
    for (const q of examForm.questions) {
      const options = q.options.split("\n").map((x) => x.trim()).filter(Boolean);
      if (!q.q.trim() || options.length < 2) { toast.error(t("edu.quizInvalid")); return; }
      questions.push({ q: q.q.trim(), options, correct: Math.min(Number(q.correct) || 0, options.length - 1) });
    }
    setBusy(true);
    try {
      await api.post("/edu/exams", { degree: examForm.degree, questions });
      toast.success(t("edu.examSaved", "Examen salvat"));
      setShowExam(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const removeExam = async (deg) => {
    if (!window.confirm(t("edu.examDeleteConfirm", "Ștergi examenul acestui grad?"))) return;
    try {
      await api.delete(`/edu/exams/${deg}`);
      if (resultsFor === deg) setResultsFor(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleResults = async (deg) => {
    if (resultsFor === deg) { setResultsFor(null); return; }
    try {
      const { data } = await api.get(`/edu/exams/${deg}/results`);
      setResults(data || []);
      setResultsFor(deg);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const seed = async () => {
    setBusy(true);
    try {
      await api.post("/edu/seed-defaults");
      toast.success(t("edu.seedOk"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { title: form.title, degree: Number(form.degree), order: Number(form.order) || 0, body: form.body, quiz: [] };
      if (form.id) await api.put(`/edu/lessons/${form.id}`, payload);
      else await api.post("/edu/lessons", payload);
      toast.success(t("edu.savedOk"));
      setShowForm(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`${t("edu.deleteConfirm")} «${l.title}»?`)) return;
    try {
      await api.delete(`/edu/lessons/${l.id}`);
      toast.success(t("edu.deletedOk"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setQuiz = (idx, patch) => {
    setExamForm((f) => ({ ...f, questions: f.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)) }));
  };

  return (
    <div data-testid="education-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <GraduationCap size={22} /> {t("edu.title")}
          </h1>
          <p className="page-subtitle">{t("edu.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          {rows.length === 0 && (
            <button className="btn-outline-gold flex items-center gap-1" onClick={seed} disabled={busy} data-testid="edu-seed-btn">
              <Sparkles size={13} /> {t("edu.seedBtn")}
            </button>
          )}
          <button className="btn-primary-gold flex items-center gap-1" onClick={openNew} data-testid="edu-new-btn">
            <Plus size={13} /> {t("edu.newBtn")}
          </button>
        </div>
      </div>

      {/* Degree exams — one per degree, unlocked after all lessons are read */}
      <div className="mt-8" data-testid="edu-exams-section">
        <h2 style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: 1, textTransform: "uppercase", color: "#8a7340" }}>
          {t("edu.examSection", "Examene pe grad")}
        </h2>
        <div className="mt-3 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
          {[1, 2, 3].map((deg) => {
            const ex = exams.find((e) => e.degree === deg);
            return (
              <div key={deg} className="lodge-card" style={{ padding: 16 }} data-testid={`edu-exam-card-${deg}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 style={{ fontWeight: 700, fontSize: "0.92rem" }}>{t(`planse.deg${deg}`)}</h3>
                  {ex ? (
                    <span style={{ fontSize: "0.72rem", color: "#8a7340", fontWeight: 700 }}>
                      {ex.questions.length} {t("edu.questions")}
                    </span>
                  ) : (
                    <span style={{ fontSize: "0.72rem", color: "#a09a88", fontStyle: "italic" }}>{t("edu.examNone", "Fără examen")}</span>
                  )}
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button className="btn-primary-gold flex items-center gap-1" onClick={() => openExam(deg)} data-testid={`edu-exam-new-${deg}`}>
                    <Plus size={12} /> {ex ? t("edu.examEdit", "Editează examenul") : t("edu.examNew", "Examen nou")}
                  </button>
                  {ex && (
                    <>
                      <button className="btn-outline-gold" onClick={() => toggleResults(deg)} data-testid={`edu-exam-results-${deg}`}>
                        {t("edu.examResults", "Rezultate")}
                      </button>
                      <button className="btn-danger" onClick={() => removeExam(deg)} data-testid={`edu-exam-delete-${deg}`}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
                {resultsFor === deg && (
                  <div className="mt-3" data-testid={`edu-exam-results-list-${deg}`}>
                    {results.length === 0 && (
                      <p style={{ fontSize: "0.78rem", color: "#8a8470", fontStyle: "italic" }}>{t("edu.examNoResults", "Nicio încercare încă")}</p>
                    )}
                    {results.map((r) => (
                      <div key={r.member_id} className="flex items-center justify-between gap-2" style={{ fontSize: "0.8rem", padding: "4px 0", borderBottom: "1px solid #efe9d8" }}>
                        <span style={{ fontWeight: 600 }}>{r.member_name}</span>
                        <span className="flex items-center gap-2">
                          {r.duration_seconds != null && (
                            <span style={{ color: "#8a8470", fontSize: "0.72rem" }}>{Math.round(r.duration_seconds / 60)} min</span>
                          )}
                          <span style={{ fontWeight: 800, color: r.passed ? "#3e6a3c" : "#8a2a2a" }}>
                            {r.score}% {r.passed ? "✓" : "✗"}
                          </span>
                          <button
                            className="btn-outline-gold" style={{ fontSize: "0.68rem", padding: "2px 8px" }}
                            onClick={async () => {
                              try {
                                await api.post(`/edu/exams/${deg}/reset/${r.member_id}`);
                                toast.success(t("edu.examResetDone", "Lecțiile au fost redeblocate"));
                                const { data } = await api.get(`/edu/exams/${deg}/results`);
                                setResults(data || []);
                              } catch (err) { toast.error(formatApiError(err)); }
                            }}
                            data-testid={`edu-exam-reset-${r.member_id}`}
                          >
                            {t("edu.examReset", "Redeblochează lecțiile")}
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mentorship: per-degree preparation tasks + member readiness */}
      <div className="mt-8" data-testid="edu-mentorship-section">
        <h2 style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: 1, textTransform: "uppercase", color: "#8a7340" }}>
          {t("edu.mentorship", "Mentorat")}
        </h2>
        <p style={{ fontSize: "0.78rem", color: "#8a8470", marginTop: 4 }}>{t("edu.mentorshipSubtitle", "Task-uri de pregătire per grad + progresul fraților către examinare")}</p>
        <MentorshipSection />
      </div>

      {rows.length === 0 && (
        <p className="mt-8" style={{ color: "#8a8470", fontStyle: "italic" }}>{t("edu.empty")}</p>
      )}

      {[1, 2, 3].map((deg) => {
        const lessons = rows.filter((l) => l.degree === deg);
        if (lessons.length === 0) return null;
        return (
          <div key={deg} className="mt-8" data-testid={`edu-degree-${deg}`}>
            <h2 style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: 1, textTransform: "uppercase", color: "#8a7340" }}>
              {t(`planse.deg${deg}`)} · {lessons.length} {t("edu.lessons")}
            </h2>
            <div className="mt-3 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
              {lessons.map((l) => (
                <div key={l.id} className="lodge-card" style={{ padding: 16 }} data-testid={`edu-lesson-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 style={{ fontWeight: 700, fontSize: "0.92rem" }}>{l.order ? `${l.order}. ` : ""}{l.title}</h3>
                    <div className="flex gap-2">
                      <button className="btn-outline-gold" onClick={() => openEdit(l)} data-testid={`edu-edit-${l.id}`}>
                        <Pencil size={12} />
                      </button>
                      <button className="btn-danger" onClick={() => remove(l)} data-testid={`edu-delete-${l.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <p style={{ fontSize: "0.78rem", color: "#6b6557", marginTop: 6, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {l.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Create / Edit modal */}
      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: "88vh", overflowY: "auto" }} data-testid="edu-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title">{form.id ? t("edu.formTitleEdit") : t("edu.formTitleNew")}</h2>
              <button className="btn-outline-gold" onClick={() => setShowForm(false)}><X size={14} /></button>
            </div>
            <form onSubmit={save} className="grid gap-3">
              <div>
                <label className="kv-label">{t("edu.fieldTitle")} *</label>
                <input
                  className="input-lodge" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="edu-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="kv-label">{t("edu.fieldDegree")}</label>
                  <select
                    className="input-lodge" value={form.degree}
                    onChange={(e) => setForm({ ...form, degree: e.target.value })}
                    data-testid="edu-degree-select"
                  >
                    <option value={1}>{t("planse.deg1")}</option>
                    <option value={2}>{t("planse.deg2")}</option>
                    <option value={3}>{t("planse.deg3")}</option>
                  </select>
                </div>
                <div>
                  <label className="kv-label">{t("edu.fieldOrder")}</label>
                  <input
                    className="input-lodge" type="number" min="0" value={form.order}
                    onChange={(e) => setForm({ ...form, order: e.target.value })}
                    data-testid="edu-order-input"
                  />
                </div>
              </div>
              <div>
                <label className="kv-label">{t("edu.fieldBody")}</label>
                <textarea
                  className="input-lodge" rows={7} value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  data-testid="edu-body-input"
                />
              </div>

              <div className="flex justify-end gap-3 mt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
                <button type="submit" className="btn-primary-gold" disabled={busy} data-testid="edu-form-save">
                  {t("edu.saveBtn")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exam create / edit modal */}
      {showExam && (
        <div className="dialog-overlay" onClick={() => setShowExam(false)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: "88vh", overflowY: "auto" }} data-testid="edu-exam-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title">{t("edu.examModalTitle", "Examen")} — {t(`planse.deg${examForm.degree}`)}</h2>
              <button className="btn-outline-gold" onClick={() => setShowExam(false)}><X size={14} /></button>
            </div>
            <form onSubmit={saveExam} className="grid gap-3">
              <div className="flex items-center justify-between">
                <label className="kv-label" style={{ marginBottom: 0 }}>{t("edu.examQuestions", "Întrebări (4 variante, prag 70%)")}</label>
                <button
                  type="button" className="btn-outline-gold"
                  onClick={() => setExamForm((f) => ({ ...f, questions: [...f.questions, { q: "", options: "", correct: 0 }] }))}
                  data-testid="exam-add-question-btn"
                >
                  <Plus size={12} /> {t("edu.addQuestion")}
                </button>
              </div>
              {examForm.questions.map((q, idx) => {
                const opts = q.options.split("\n").map((x) => x.trim()).filter(Boolean);
                return (
                  <div key={idx} style={{ padding: 12, background: "#faf7f0", borderRadius: 8, border: "1px solid #e6e0cf" }} data-testid={`exam-question-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <input
                        className="input-lodge" placeholder={t("edu.fieldQuestion")} value={q.q}
                        onChange={(e) => setQuiz(idx, { q: e.target.value })}
                        data-testid={`exam-question-input-${idx}`}
                      />
                      <button
                        type="button" className="btn-danger"
                        onClick={() => setExamForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }))}
                        data-testid={`exam-remove-question-${idx}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <textarea
                      className="input-lodge mt-2" rows={4} placeholder={t("edu.fieldOptions")} value={q.options}
                      onChange={(e) => setQuiz(idx, { options: e.target.value })}
                      data-testid={`exam-options-input-${idx}`}
                    />
                    <div className="mt-2">
                      <label className="kv-label">{t("edu.fieldCorrect")}</label>
                      <select
                        className="input-lodge" value={q.correct}
                        onChange={(e) => setQuiz(idx, { correct: Number(e.target.value) })}
                        data-testid={`exam-correct-select-${idx}`}
                      >
                        {opts.map((o, i) => <option key={i} value={i}>{o}</option>)}
                        {opts.length === 0 && <option value={0}>—</option>}
                      </select>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-end gap-3 mt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setShowExam(false)}>{t("common.cancel")}</button>
                <button type="submit" className="btn-primary-gold" disabled={busy || examForm.questions.length === 0} data-testid="exam-form-save">
                  {t("edu.saveBtn")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
